/**
 * @module NotificationPanel
 * @package @geenius/notifications-solidjs-css
 * @description Renders the compact notification drawer used by the SolidJS
 * vanilla CSS variant.
 */

import type { Notification } from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { For, onCleanup, onMount, Show } from 'solid-js'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'
import { EmptyNotifications } from './EmptyNotifications'
import { NotificationItem } from './NotificationItem'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
    .filter((element) => element.getAttribute('aria-hidden') !== 'true')
}

/**
 * Props for the SolidJS CSS notification panel.
 */
export interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAll?: () => void
  onClose?: () => void
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onViewAll?: () => void
  panelId?: string
  title?: string
  class?: string
}

/**
 * Renders the notification drawer panel.
 *
 * @param props Drawer props including notification data and action handlers.
 * @returns The drawer panel markup.
 */
export function NotificationPanel(props: NotificationPanelProps): JSX.Element {
  const { t } = createNotificationCopy()
  let panelRef: HTMLElement | undefined
  const unreadCount = () =>
    props.notifications.filter((notification) => notification.status === 'unread')
      .length
  const panelId = () => props.panelId ?? 'notifications-panel'
  const titleId = () => `${panelId()}-title`

  onMount(() => {
    const panel = panelRef

    if (!panel) {
      return
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusable = getFocusableElements(panel)

    ;(focusable[0] ?? panel).focus()

    onCleanup(() => {
      previousFocus?.focus()
    })
  })

  const handleKeyDown = (event: KeyboardEvent): void => {
    const panel = panelRef

    if (!panel) {
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      props.onClose?.()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const focusable = getFocusableElements(panel)

    if (focusable.length === 0) {
      event.preventDefault()
      panel.focus()
      return
    }

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstFocusable || activeElement === panel) {
        event.preventDefault()
        lastFocusable.focus()
      }
      return
    }

    if (activeElement === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  return (
    <section
      ref={panelRef}
      id={panelId()}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId()}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      class={cx('notifications__panel', 'gm-motion', 'gm-motion--scale-in', props.class)}
    >
      <div class={cx('notifications__panel-header')}>
        <span id={titleId()} class={cx('notifications__panel-title')}>
          {props.title ?? t('notifications.common.title')}
          <span class={cx('notifications__badge', 'notifications__badge--large')}>
            {unreadCount()}
          </span>
        </span>
        <div class={cx('notifications__panel-actions')}>
          <button
            type="button"
            class={cx('notifications__panel-mark-all')}
            onClick={() => props.onMarkAll?.()}
            disabled={!props.onMarkAll || unreadCount() === 0}
          >
            {t('notifications.common.markAllRead')}
          </button>
          <button
            type="button"
            class={cx('notifications__panel-close')}
            onClick={() => props.onClose?.()}
            aria-label={t('notifications.panel.closeAria')}
          >
            ✕
          </button>
        </div>
      </div>
      <div class={cx('notifications__panel-body')}>
        <Show when={props.notifications.length > 0} fallback={<EmptyNotifications />}>
          <ul class={cx('notifications__panel-list')}>
            <For each={props.notifications}>
              {(notification) => (
                <li>
                  <NotificationItem
                    notification={notification}
                    onRead={props.onRead}
                    onDismiss={props.onDismiss}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
      <Show when={props.notifications.length > 0 && props.onViewAll}>
        <div class={cx('notifications__panel-footer')}>
          <button
            type="button"
            class={cx('notifications__panel-view-all')}
            onClick={() => props.onViewAll?.()}
          >
            {t('notifications.common.viewAll')}
          </button>
        </div>
      </Show>
    </section>
  )
}
